import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

import { computeDaysRemainingInMonth, type NetworkStatus } from './types';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconClock, IconInfoCircle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ObjectiveCardProps {
  status: NetworkStatus;
  setOpenObjectiveInfo: (open: boolean) => void;
}

export const ObjectiveCard = function ObjectiveCard({ status, setOpenObjectiveInfo }: ObjectiveCardProps) {
  const { t } = useTranslation('streak');
  const ratio = Math.min(1, status.confirmedTotal / Math.max(1, status.targetM));
  const percent = Math.round(ratio * 100);
  const daysRemaining = computeDaysRemainingInMonth(new Date());

  return (
    <Column backgroundColor="white" borderRadius="radius.lg" gap="spacing.general.x2" padding="spacing.general.x4">
      <Row alignItems="center" justifyContent="space-between">
        <Row alignItems="center" gap="spacing.general.x1">
          <Text fontSize={15} fontWeight="600">
            {t('objective')}
          </Text>
          <Pressable hitSlop={10} onPress={() => setOpenObjectiveInfo(true)}>
            <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} size={24} color={theme.colors['text.tertiary']} />
          </Pressable>
        </Row>
      </Row>

      <Row alignItems="baseline" gap="spacing.general.x1">
        <Text fontSize={32} fontWeight="bold">
          {status.confirmedTotal}
        </Text>
        <Text color="text.tertiary" fontSize={16}>
          /{status.targetM} {t('shares')}
        </Text>
      </Row>

      <Column
        backgroundColor="surface.inverse.tertiary-neutral"
        borderRadius="radius.lg"
        height={18}
        justifyContent="center"
      >
        <Column
          backgroundColor="surface.brand.secondary-3.default"
          borderRadius="radius.lg"
          height={18}
          style={{ width: `${(ratio * 100).toFixed(1)}%` as `${number}%` }}
        />
        <Text
          color={percent >= 50 ? 'white' : 'surface.brand.secondary-3.default'}
          fontSize={11}
          fontWeight="bold"
          style={{ position: 'absolute', alignSelf: 'center' }}
        >
          {percent}%
        </Text>
        <Row
          alignItems="center"
          gap="spacing.general.x1"
          style={{
            position: 'absolute',
            right: theme.spacing['spacing.general.x1'],
            top: -theme.spacing['spacing.general.x5'],
          }}
        >
          <IconClock strokeWidth={ICON_STROKE_WIDTH} size={14} color={theme.colors['text.tertiary']} />
          <Text color="text.tertiary" fontSize={13}>
            {t('daysRemaining', { count: daysRemaining })}
          </Text>
        </Row>
      </Column>

      <Text fontSize={14} fontWeight="500">
        {t('myContribution', { count: status.individualConfirmedTotal })}
      </Text>
    </Column>
  );
};
